import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Mail, Loader2, ArrowRight, KeyRound, CheckCircle2, Eye, Ghost, MapPin, Send } from 'lucide-react';
import { API } from '../lib/constants';

const GHOST_X_URL = 'https://x.com/GetOffersz';

const formatError = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const Claim = () => {
  const navigate = useNavigate();
  const { username } = useParams();
  const [target, setTarget] = useState(null);
  const [loadingTarget, setLoadingTarget] = useState(true);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/claim/${username}`)
      .then(r => setTarget(r.data))
      .catch(() => setTarget(null))
      .finally(() => setLoadingTarget(false));
  }, [username]);

  const requestCode = async () => {
    setRequesting(true);
    try {
      const res = await axios.post(`${API}/api/claim/${username}/request-code`);
      setRequested(true);
      toast.success(res.data?.message || 'Request sent! DM @GetOffersz for your code.');
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setRequesting(false);
      window.open(GHOST_X_URL, '_blank', 'noopener,noreferrer');
    }
  };

  const claim = async () => {
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    if (!/^\d{6}$/.test(code)) { toast.error('Enter the 6-digit code'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/claim/${username}`, { email, code }, { withCredentials: true });
      if (res.data.success) {
        localStorage.setItem('userId', res.data.user_id);
        localStorage.setItem('userEmail', res.data.email);
        localStorage.setItem('username', res.data.username);
        toast.success('Your page is live! Welcome to Offersz.');
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingTarget) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="h-8 w-8 text-[#16A34A] animate-spin" /></div>;
  }

  if (!target) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4 text-center">
        <div>
          <h1 className="text-2xl font-black mb-2">Page not found</h1>
          <p className="text-white/50 mb-6">This claim link doesn't match a profile.</p>
          <Button onClick={() => navigate('/')} className="bg-[#0F5132] hover:bg-[#0B3B24] rounded-xl">Go home</Button>
        </div>
      </div>
    );
  }

  if (target.already_claimed) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4 text-center">
        <div>
          <CheckCircle2 className="h-12 w-12 text-[#16A34A] mx-auto mb-4" />
          <h1 className="text-2xl font-black mb-2">Already claimed</h1>
          <p className="text-white/50 mb-6">This page has already been claimed. Sign in to manage it.</p>
          <Button onClick={() => navigate('/signin')} className="bg-[#0F5132] hover:bg-[#0B3B24] rounded-xl">Sign in</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white px-4 py-10 sm:py-14" data-testid="claim-page">
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <img src="/offersz-lockup.png" alt="Offersz" data-testid="claim-wordmark" className="w-36 h-auto mx-auto mb-6" />
          <h1 className="text-2xl sm:text-3xl font-black mb-2">Claim your free recruiting page</h1>
          <p className="text-white/50 text-sm">We built a page for <span className="text-white font-bold">{target.name}</span> at offersz.co/{target.username}.</p>
          <button onClick={() => navigate(`/preview/${username}`)} data-testid="claim-preview-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#16A34A] hover:text-[#22c55e] border border-[#16A34A]/40 hover:border-[#16A34A] rounded-full px-5 py-2.5 transition-colors">
            <Eye className="h-4 w-4" /> See a preview of your page first
          </button>
        </div>

        {/* STEALTH MODE — ghost theme */}
        <div data-testid="stealth-mode-card"
          className="relative overflow-hidden rounded-3xl border border-[#16A34A]/30 mb-6 bg-[#0a0a0a]">
          <div className="relative h-56 w-full overflow-hidden">
            <img src="/stealth-ghost.jpg" alt="Offersz stealth ghost"
              className="w-full h-full object-cover object-[50%_32%] brightness-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/20 to-transparent" />
            <span className="absolute top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-[#4ade80] bg-black/60 backdrop-blur border border-[#16A34A]/40 rounded-full px-3 py-1">
              <Ghost className="h-3.5 w-3.5" /> Stealth Mode
            </span>
          </div>
          <div className="relative px-6 pb-6 -mt-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-black leading-tight">
              Coaches can't recruit a <span className="text-[#4ade80]">ghost.</span>
            </h2>
            <p className="mt-3 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-white/70">
              <MapPin className="h-4 w-4 text-[#4ade80]" /> Looking for early ballersz in Indianapolis to join.
            </p>

            <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-4">
              <p className="text-sm text-white/80 leading-relaxed">
                Follow <span className="font-bold text-white">@GetOffersz</span> and <span className="font-bold text-white">send us a DM</span> — we'll send you a special code to claim your profile.
              </p>
              <Button onClick={requestCode} disabled={requesting} data-testid="follow-dm-btn"
                className="mt-4 w-full bg-[#16A34A] hover:bg-[#15803d] text-white font-bold h-12 rounded-xl text-base">
                {requesting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Send className="mr-2 h-5 w-5" />}
                Follow + DM @GetOffersz
              </Button>
              {requested && (
                <p data-testid="request-confirmed" className="mt-3 text-xs text-[#4ade80] font-semibold inline-flex items-center justify-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> We got your request — DM us and we'll send your code.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Already have a code */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
          <h3 className="text-sm font-black uppercase tracking-wider text-white/50 mb-4 text-center">Already have your code?</h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-white/70 flex items-center gap-2"><Mail className="h-4 w-4" /> Your email</label>
              <Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@email.com"
                data-testid="claim-email-input" className="bg-white/5 border-white/10 text-white h-12 rounded-xl" />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-white/70 flex items-center gap-2"><KeyRound className="h-4 w-4" /> 6-digit code</label>
              <Input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric"
                autoComplete="one-time-code" placeholder="123456" data-testid="claim-code-input"
                onKeyPress={e => e.key === 'Enter' && claim()}
                className="bg-white/5 border-white/10 text-white h-14 rounded-xl text-center text-3xl tracking-[0.5em] font-bold" />
            </div>

            <Button onClick={claim} disabled={!email || code.length !== 6 || submitting} data-testid="claim-submit-btn"
              className="w-full bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold h-12 rounded-xl text-lg">
              {submitting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <ArrowRight className="mr-2 h-5 w-5" />}
              Claim & go live
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Claim;
